<template>
    <Overlay :show="loading">
        <div class="manifest-ui import-run-details">
            <header class="import-run-details__header mb-2">
                <div>
                    <div class="d-flex align-items-center mb-25">
                        <h2 class="mb-0 mr-75">{{ headerTitle }}</h2>
                        <b-badge v-if="manifest" :variant="manifestStatusVariant">
                            {{ manifest.status }}
                        </b-badge>
                    </div>
                    <p class="text-muted mb-0">{{ headerSubtitle }}</p>
                </div>
                <div class="d-flex align-items-center">
                    <b-button variant="primary" class="mr-1" @click="startImport">
                        <feather-icon icon="PlusIcon" size="16" class="mr-50" />
                        New import
                    </b-button>
                    <b-button variant="outline-secondary" @click="backToRuns">
                        Back
                    </b-button>
                </div>
            </header>

            <b-alert v-if="loadError" variant="danger" show>
                <div class="alert-body d-flex align-items-center justify-content-between">
                    <span>The import run could not be loaded.</span>
                    <b-button size="sm" variant="flat-danger" @click="loadJob">
                        Retry
                    </b-button>
                </div>
            </b-alert>

            <b-card v-if="job" title="Import execution">
                <div class="import-run-details__metadata mb-2">
                    <div>
                        <span>Manifest</span>
                        <strong v-if="manifest" :title="manifest.description || ''">
                            {{ manifest.name }}
                        </strong>
                        <strong v-else>Unavailable</strong>
                    </div>
                    <div>
                        <span>Version</span>
                        <strong>{{ manifest ? `Revision ${manifest.revision}` : '—' }}</strong>
                    </div>
                    <div>
                        <span>Import type</span>
                        <strong>{{ manifest ? manifestTypeTitle : '—' }}</strong>
                    </div>
                    <div>
                        <span>Owner context</span>
                        <strong>{{ manifest ? manifest.ownerContext : '—' }}</strong>
                    </div>
                    <div>
                        <span>Created at</span>
                        <strong>{{ formatDate(job.createdAt) }}</strong>
                    </div>
                    <div>
                        <span>Initiated by</span>
                        <strong>{{ job.initiatingUserId || '—' }}</strong>
                    </div>
                </div>

                <ImportJobProgress
                    :job="job"
                    :refreshing="refreshing"
                    :poll-error="pollError"
                    @refresh="refreshJob(true)"
                    @restart="startImport"
                />

                <details class="import-run-details__technical mt-2">
                    <summary>Technical references</summary>
                    <div class="import-run-details__technical-grid mt-1">
                        <div>
                            <span>Job</span>
                            <strong :title="job.id">{{ job.id }}</strong>
                        </div>
                        <div>
                            <span>Control run</span>
                            <strong :title="job.controlRunId">{{ job.controlRunId }}</strong>
                        </div>
                        <div>
                            <span>Plan artifact</span>
                            <strong :title="job.planArtifactId">{{ job.planArtifactId }}</strong>
                        </div>
                    </div>
                </details>
            </b-card>
        </div>
    </Overlay>
</template>

<script>
import {
    BAlert,
    BBadge,
    BButton,
    BCard,
} from 'bootstrap-vue'
import {ImportManifestApi} from '@core/api'
import Overlay from '@core/components/common/Overlay.vue'
import backendResponse from '@core/mixins/ui/backend-response'
import {getImportManifestTitle} from '../manifests/manifestContexts'
import ImportJobProgress from '../runner/ImportJobProgress.vue'

const JOB_POLL_INTERVAL = 2000
const TERMINAL_JOB_STATUSES = ['completed', 'failed']

export default {
    name: 'ImportRunDetails',
    components: {
        BAlert,
        BBadge,
        BButton,
        BCard,
        ImportJobProgress,
        Overlay,
    },
    mixins: [backendResponse],
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            job: null,
            jobPollTimer: null,
            loadError: false,
            loading: false,
            pollError: null,
            refreshing: false,
            requestId: 0,
        }
    },
    computed: {
        manifest() {
            return this.job && this.job.manifest ? this.job.manifest : null
        },
        headerTitle() {
            return this.manifest ? this.manifest.name : 'Import run'
        },
        headerSubtitle() {
            if (!this.job) {
                return this.id
            }
            if (!this.manifest) {
                return `Manifest unavailable · Control run ${this.job.controlRunId || '—'}`
            }
            return `${this.manifestTypeTitle} · Revision ${this.manifest.revision} · ${this.job.id}`
        },
        manifestTypeTitle() {
            return this.manifest
                ? getImportManifestTitle(this.manifest.manifestType)
                : 'Import'
        },
        manifestStatusVariant() {
            return {
                active: 'light-success',
                draft: 'light-warning',
                outdated: 'light-secondary',
            }[this.manifest && this.manifest.status] || 'light-secondary'
        },
    },
    watch: {
        id() {
            this.loadJob()
        },
    },
    created() {
        this.loadJob()
    },
    beforeDestroy() {
        this.cancelPolling()
    },
    methods: {
        loadJob() {
            this.cancelPolling()
            this.loading = true
            this.loadError = false
            this.pollError = null
            this.job = null
            const requestId = ++this.requestId

            return ImportManifestApi.getImportJob(this.id)
                .then(response => {
                    if (requestId !== this.requestId) {
                        return
                    }
                    this.job = response.data
                    this.schedulePoll()
                })
                .catch(error => {
                    if (requestId === this.requestId) {
                        this.loadError = true
                        this.handleBackendError(error)
                    }
                })
                .finally(() => {
                    if (requestId === this.requestId) {
                        this.loading = false
                    }
                })
        },
        refreshJob(showLoading = false) {
            if (!this.job || this.isTerminal(this.job)) {
                return Promise.resolve()
            }

            this.clearPollTimer()
            const requestId = ++this.requestId
            if (showLoading) {
                this.refreshing = true
            }

            return ImportManifestApi.getImportJob(this.id)
                .then(response => {
                    if (requestId !== this.requestId) {
                        return
                    }
                    this.job = response.data
                    this.pollError = null
                })
                .catch(() => {
                    if (requestId === this.requestId) {
                        this.pollError = 'The latest progress could not be loaded. Polling will retry automatically.'
                    }
                })
                .finally(() => {
                    if (requestId !== this.requestId) {
                        return
                    }
                    this.refreshing = false
                    this.schedulePoll()
                })
        },
        schedulePoll() {
            this.clearPollTimer()
            if (!this.job || this.isTerminal(this.job)) {
                return
            }
            this.jobPollTimer = window.setTimeout(() => {
                this.jobPollTimer = null
                this.refreshJob(false)
            }, JOB_POLL_INTERVAL)
        },
        clearPollTimer() {
            if (this.jobPollTimer) {
                window.clearTimeout(this.jobPollTimer)
                this.jobPollTimer = null
            }
        },
        cancelPolling() {
            this.clearPollTimer()
            this.requestId += 1
        },
        isTerminal(job) {
            return Boolean(job && TERMINAL_JOB_STATUSES.includes(job.status))
        },
        startImport() {
            this.$router.push({name: 'administration-import'})
        },
        backToRuns() {
            this.$router.push({name: 'administration-import-runs'})
        },
        formatDate(value) {
            return value ? this.$moment(value).format('YYYY-MM-DD HH:mm') : '—'
        },
    },
}
</script>

<style lang="scss">
@import '../manifests/theme';
</style>

<style lang="scss" scoped>
.import-run-details__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.import-run-details__header p {
    overflow-wrap: anywhere;
}

.import-run-details__metadata {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    overflow: hidden;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    background: #f8f8f8;
}

.import-run-details__metadata > div {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: .25rem;
    padding: .75rem 1rem;
}

.import-run-details__metadata > div:nth-child(3n + 2),
.import-run-details__metadata > div:nth-child(3n + 3) {
    border-left: 1px solid #ebe9f1;
}

.import-run-details__metadata > div:nth-child(n + 4) {
    border-top: 1px solid #ebe9f1;
}

.import-run-details__metadata span {
    color: var(--manifest-secondary-text-color, #45434c);
    font-size: .8rem;
}

.import-run-details__technical {
    border-top: 1px solid #ebe9f1;
    color: var(--manifest-secondary-text-color, #45434c);
}

.import-run-details__technical summary {
    padding-top: 1rem;
    cursor: pointer;
    font-weight: 600;
}

.import-run-details__technical-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: .75rem;
}

.import-run-details__technical-grid > div {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: .25rem;
    padding: .75rem 1rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    background: #f8f8f8;
}

.import-run-details__technical-grid span {
    font-size: .8rem;
}

.import-run-details__technical-grid strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.import-run-details__metadata strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

@media (max-width: 767.98px) {
    .import-run-details__header {
        align-items: flex-start;
        flex-direction: column;
    }

    .import-run-details__metadata {
        grid-template-columns: 1fr;
    }

    .import-run-details__technical-grid {
        grid-template-columns: 1fr;
    }

    .import-run-details__metadata > div:nth-child(n + 2) {
        border-top: 1px solid #ebe9f1;
        border-left: 0;
    }
}
</style>
